import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  useGetAssetsByIdQuery,
  useUpdateAssetsMutation
} from '@ports/internal-services/asset/assetSlice';
import { MediaInfoSidebarProps } from '@business/interfaces/photo';
import moment from 'moment';
import { DATE_FORMAT_MMM_DD_YYYY_HH_MM } from '@utils/constants/globals';
import { Button, Input } from '@business/workflows/ComponentUI';
import { SVGAssets } from '@business/assets/vectors';
import {
  useAddPeopleAssetMutation,
  useGetPeopleByNameQuery
} from '@ports/internal-services/people/peopleSlice';
import { SearchableSelectInput } from '@business/workflows/ComponentUI/SearchableInput';
import { Avatar } from '@business/workflows/ComponentUI/Avatar';
import { showToast } from '@business/workflows/ComponentUI/Toast/Toast';
import { getPermissions } from '@ports/common/authStateSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { incrementAssetRefreshCounter } from '@ports/common/sessionStateSlice';
import EditTagsModal from './EditTagsModal';

const MediaInfoSidebar: React.FC<MediaInfoSidebarProps> = ({
  isOpen,
  onClose,
  assetId,
  setIsUpdateFilters,
  isSharing,
  groupId,
  refetch
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const permissions = useSelector(getPermissions);
  const currentUser = useSelector(getUserData);
  const canTagPeople = permissions.includes('photo:tag') && !isSharing;
  const canEditDate = permissions.includes('photo:update') && !isSharing;
  const canEditThings = permissions.includes('things:update') && !isSharing;
  const [isEditTagsModalOpen, setIsEditTagsModalOpen] =
    useState<boolean>(false);
  const [isOpenAddTag, setIsOpenAddTag] = useState<boolean>(false);
  const [selectedPeople, setSelectedPeople] = useState<
    Array<{ id: string; name: string; profilePicUrl: string }>
  >([]);
  const [peopleSearchText, setPeopleSearchText] = useState<string>('');
  const [selectKey, setSelectKey] = useState<number>(0);
  const [isEditingTakenDate, setIsEditingTakenDate] = useState(false);
  const [takenDateInput, setTakenDateInput] = useState('');

  // Fetch asset data by ID
  const { data: assetDetailsData, refetch: refetchAsset } =
    useGetAssetsByIdQuery({ id: assetId }, { skip: !assetId });

  const { data: peopleDataForSearch, refetch: refetchPeople } =
    useGetPeopleByNameQuery(
      {
        search: peopleSearchText
      },
      { skip: !peopleSearchText }
    );

  const [TagPeople] = useAddPeopleAssetMutation();
  const [UpdateAssets] = useUpdateAssetsMutation();

  useEffect(() => {
    if (peopleSearchText) {
      refetchPeople();
    }
  }, [peopleSearchText]);

  const tagSubmitHandler = async (peopleId: string[]) => {
    try {
      await TagPeople({
        createPeopleAsset: { assetId, peopleId, removeOld: true }
      }).unwrap();
      dispatch(incrementAssetRefreshCounter());
      // After successful tagging, refetch the asset details to get updated tags
      refetch();
      refetchAsset();
    } catch (error) {
      console.error('Error tagging person to asset:', error);
    }
  };

  const assetData = assetDetailsData?.data;
  const mediaType = assetData?.type || 'image';

  // Format file size
  const formatSize = (bytes?: number) => {
    if (!bytes) return t('mediaInfoSidebar.unknown');
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };
  const fileSize = formatSize(assetData?.size);

  // Format dates (display in local time)
  const formatDate = (dateString?: string) => {
    return dateString
      ? moment.utc(dateString).local().format(DATE_FORMAT_MMM_DD_YYYY_HH_MM)
      : '';
  };

  const createdDate = formatDate(assetData?.createdAt);
  const modifiedDate = formatDate(assetData?.updatedAt);
  const takenDate = formatDate(assetData?.takenDate);

  const handleSaveTakenDate = () => {
    // Convert local datetime-local input to UTC ISO string for API
    let utcDateString = takenDateInput
      ? moment(takenDateInput).utc().toISOString()
      : '';
    const dataObj = {
      id: assetId,
      assetsCreateObject: {
        takenDate: utcDateString
      }
    };
    UpdateAssets(dataObj)
      .then(res => {
        if (res) {
          dispatch(incrementAssetRefreshCounter());
          setIsEditingTakenDate(false);
          showToast({
            type: 'success',
            isAlert: true,
            alertProps: {
              type: 'success',
              message: t('mediaInfoSidebar.takenDateSuccess')
            }
          });
        }
      })
      .catch(() => {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: t('mediaInfoSidebar.takenDateError')
          }
        });
      });
  };

  const lastOpenDate = formatDate(assetData?.assetViewDetails?.updatedAt);

  const handleOpenEditTags = () => {
    setIsEditTagsModalOpen(true);
  };

  const handleCloseEditTags = () => {
    setIsEditTagsModalOpen(false);
    refetchAsset(); // Refresh the data
  };

  const handleTagsUpdated = () => {
    // Refetch asset data to get updated tags
    refetchAsset();
    setIsUpdateFilters?.(true);
  };

  const currentTags =
    assetData?.photoTags?.map((tag: any) => ({
      id: tag.id,
      tagName: tag.tagName
    })) || [];

  const handleEditPeople = () => {
    setIsOpenAddTag(!isOpenAddTag);
  };

  const handleAddPerson = (id: string, name: string, profilePicUrl: string) => {
    if (!selectedPeople.find(person => person.id === id)) {
      setSelectedPeople(prev => [...prev, { id, name, profilePicUrl }]);
    }

    setPeopleSearchText('');
    setSelectKey(prev => prev + 1);
  };

  const handleRemovePerson = (id: string) => {
    setSelectedPeople(selectedPeople.filter(person => person.id !== id));
  };

  // Format people options for SearchableSelectInput
  const peopleOptions =
    peopleDataForSearch?.data?.rows?.map((person: any) => ({
      id: person.id,
      name: person.fullName || person.name,
      profilePicUrl: person?.profileImage
    })) || [];

  useEffect(() => {
    if (assetData?.people?.length) {
      const mappedPeople = assetData.people
        .map((item: any) => {
          // Handle both nested (item.person) and flat structures
          const person = item.person || item;
          return {
            id: person?.id,
            name: person?.fullName || person?.name,
            profilePicUrl: person?.profileImage
          };
        })
        .filter(
          (person: any) =>
            person.id &&
            person.name !== null &&
            person.name !== undefined &&
            person.name !== ''
        );

      setSelectedPeople(mappedPeople);
    } else {
      setSelectedPeople([]);
    }
  }, [assetDetailsData]);

  const location = [
    assetData?.locationDetails?.city,
    assetData?.locationDetails?.region,
    assetData?.locationDetails?.country
  ]
    .filter(Boolean)
    .join(', ');

  if (!isOpen && !isEditTagsModalOpen) return null;

  return (
    <>
      {/* Backdrop */}
      {isOpen && !isEditTagsModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-[60] transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      {isOpen && !isEditTagsModalOpen && (
        <div className="fixed right-0 top-0 h-full w-72 bg-white shadow-2xl z-[99] transform transition-transform duration-300 ease-in-out overflow-y-auto">
          {/* Content */}
          <div className="p-6">
            {/* Header with Close Button */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-1">
                  {t('mediaInfoSidebar.closerLook')}
                </h2>
                <p className="text-xs text-gray-500">
                  {t('mediaInfoSidebar.closerLookDescription')}
                </p>
              </div>
              <Button
                onClick={onClose}
                className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                btnStyle="no-outline">
                <SVGAssets.CloseBtn className="text-black" />
              </Button>
            </div>

            {/* Owner Section */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <SVGAssets.Owner className="w-5 h-5 text-gray-700" />
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('mediaInfoSidebar.owner')}
                </h3>
              </div>
              <div className="flex items-center gap-3 ml-7">
                <div className="w-10 h-10 bg-gray-300  rounded-full flex items-center justify-center overflow-hidden">
                  {assetData?.user?.userDetail?.profileImage ? (
                    <Avatar
                      alt={
                        assetData?.user?.userDetail?.firstName +
                        ' ' +
                        assetData?.user?.userDetail?.lastName
                      }
                      imgClassName="rounded-full w-12 h-12 border border-gray-300 mb-2"
                      src={assetData?.user?.userDetail?.profileImage}
                      size="h-12 w-12"
                      isHeader={true}
                    />
                  ) : (
                    <span className="text-sm font-semibold text-gray-600 uppercase">
                      {assetData?.user?.userDetail?.firstName?.charAt(0) || 'U'}
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-900">
                  {assetData?.user?.userDetail?.firstName +
                    ' ' +
                    assetData?.user?.userDetail?.lastName ||
                    t('mediaInfoSidebar.unknown')}
                </span>
              </div>
            </div>

            {/* People Section */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                {/* LEFT — Icon + Title */}
                <div className="flex items-center gap-2">
                  <SVGAssets.PeopleIcon className="w-5 h-5 text-gray-700" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('mediaInfoSidebar.people')}
                  </h3>
                </div>

                {/* RIGHT — Edit button (photo:tag permission) */}
                {canTagPeople &&
                  (!groupId ||
                    currentUser?.userId === assetData?.createdBy) && (
                    <Button
                      btnStyle="link"
                      type="button"
                      onClick={handleEditPeople}
                      className="p-1 rounded hover:bg-gray-100 transition-colors">
                      <SVGAssets.EditIcon className="w-4 h-4 text-gray-600" />
                    </Button>
                  )}
              </div>

              {isOpenAddTag && (
                <div className="mb-3 ml-7 media-sidebar-select-fix">
                  <SearchableSelectInput
                    key={selectKey}
                    name="personName"
                    placeholder={t('enterName')}
                    isWhiteTheme
                    value={peopleSearchText}
                    options={peopleOptions}
                    isSelectOnly={true}
                    onSearch={(value: string) => setPeopleSearchText(value)}
                    onChange={({
                      id,
                      name,
                      profilePicUrl
                    }: {
                      id: any;
                      name: string;
                      profilePicUrl: string;
                    }) => {
                      if (id && name) {
                        handleAddPerson(id, name, profilePicUrl);
                      }
                    }}
                  />
                </div>
              )}

              <div className="space-y-3 ml-7">
                {selectedPeople.length > 0 ? (
                  selectedPeople.map(person => (
                    <div
                      key={person.id}
                      className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
                          <Avatar
                            alt={person.name}
                            imgClassName="rounded-full w-12 h-12 border border-gray-300 mb-2"
                            src={person.profilePicUrl}
                            size="h-12 w-12"
                            isHeader={true}
                          />
                        </div>
                        <div>
                          <p className="text-sm text-gray-900">{person.name}</p>
                        </div>
                      </div>
                      {isOpenAddTag && (
                        <Button
                          btnStyle="link"
                          type="button"
                          onClick={() => handleRemovePerson(person.id)}
                          className="p-1 rounded hover:bg-gray-100 transition-colors">
                          <SVGAssets.TrashIcon className="w-4 h-4 text-red-600" />
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    {t('mediaInfoSidebar.noPeople')}
                  </p>
                )}
              </div>

              {isOpenAddTag && (
                <div className="mt-4 ml-7">
                  <Button
                    onClick={() => {
                      const peopleIds = selectedPeople.map(person => person.id);
                      tagSubmitHandler(peopleIds);
                      setIsOpenAddTag(false);
                    }}
                    className="w-full bg-lime-500 hover:bg-lime-600 text-white py-2 px-4 rounded transition-colors"
                    btnStyle="primary">
                    {t('submit')}
                  </Button>
                </div>
              )}
            </div>

            {/* Location Section */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SVGAssets.Locations className="w-5 h-5 text-gray-700" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('mediaInfoSidebar.location')}
                  </h3>
                </div>
              </div>
              <p className="text-sm text-gray-600 ml-7">
                {assetData?.locationDetails?.address
                  ? location
                  : assetData?.location ||
                    t('mediaInfoSidebar.noLocationAvailable')}
              </p>
            </div>

            {/* Last Modified Date */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('mediaInfoSidebar.lastModifiedDate')}
              </h3>
              <p className="text-sm text-gray-600">{modifiedDate}</p>
            </div>

            {/* Last Open */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('mediaInfoSidebar.lastOpen')}
              </h3>
              <p className="text-sm text-gray-600">{lastOpenDate}</p>
            </div>

            {/* Creation Date */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  {t('mediaInfoSidebar.uploadedDate')}
                </h3>
              </div>
              <p className="text-sm text-gray-600">{createdDate}</p>
            </div>

            {/* Creation Date */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  {t('mediaInfoSidebar.takenDate')}
                  {canEditDate &&
                    (!groupId ||
                      currentUser?.userId === assetData?.createdBy) && (
                      <Button
                        btnStyle="no-outline"
                        className="ml-1 p-1 hover:bg-gray-100 rounded"
                        onClick={() => {
                          setIsEditingTakenDate(true);
                          if (assetData?.takenDate) {
                            // Convert to 'MM-DD-YYYYTHH:mm' for datetime-local input
                            const dt = moment
                              .utc(assetData.takenDate)
                              .local()
                              .format('YYYY-MM-DDTHH:mm');
                            setTakenDateInput(dt);
                          } else {
                            setTakenDateInput('');
                          }
                        }}>
                        <SVGAssets.EditIcon className="w-4 h-4 text-gray-600" />
                      </Button>
                    )}
                </h3>
              </div>
              {isEditingTakenDate ? (
                <>
                  <Input
                    type="datetime-local"
                    isWhiteTheme
                    required
                    value={takenDateInput}
                    max={moment().format('YYYY-MM-DDTHH:mm')}
                    onChange={e => setTakenDateInput(e.target.value)}
                    className="w-auto border border-gray-300 rounded px-2 py-1 text-sm z-[9999]"
                  />
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <Button
                      btnStyle="primary"
                      className="px-3 py-1 text-sm"
                      onClick={handleSaveTakenDate}>
                      {t('save')}
                    </Button>
                    <Button
                      btnStyle="link"
                      type="button"
                      className="px-3 py-1 !bg-[#F9FEE9] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm z-[9999]"
                      onClick={() => setIsEditingTakenDate(false)}>
                      {t('cancel')}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-sm text-gray-600">{takenDate}</p>
              )}
            </div>

            {/* Type */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('mediaInfoSidebar.type')}
              </h3>
              <p className="text-sm text-gray-600">
                {assetData?.type || mediaType || t('mediaInfoSidebar.unknown')}
              </p>
            </div>

            {/* Size */}
            <div className="mb-6 pb-6 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t('mediaInfoSidebar.size')}
              </h3>
              <p className="text-sm text-gray-600">{fileSize}</p>
            </div>

            {/* Things (Tags) */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <SVGAssets.Things className="w-5 h-5 text-gray-700" />
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('mediaInfoSidebar.things')}
                  </h3>
                </div>
                {canEditThings &&
                  (!groupId ||
                    currentUser?.userId === assetData?.createdBy) && (
                    <Button
                      onClick={handleOpenEditTags}
                      className="text-gray-500 hover:text-gray-700"
                      btnStyle="no-outline">
                      <SVGAssets.PencilIcon className="w-4 h-4" />
                    </Button>
                  )}
              </div>
              {assetData?.photoTags && assetData.photoTags.length > 0 ? (
                <div className="flex flex-wrap gap-2 ml-7">
                  {assetData.photoTags.map((tag: any, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-lime-200 text-gray-800 text-sm rounded-full">
                      {tag.tagName || t('mediaInfoSidebar.unknown')}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 ml-7">
                  {t('mediaInfoSidebar.noTags')}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Tags Modal */}
      <EditTagsModal
        isOpen={isEditTagsModalOpen}
        onClose={handleCloseEditTags}
        assetId={assetId}
        currentTags={currentTags}
        onTagsUpdated={handleTagsUpdated}
      />
    </>
  );
};

export default MediaInfoSidebar;
